"use client";
import React from "react";
import useSofia from "@/hooks/useSofia";
import css from "./SofiaBot.module.css";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { MarkdownLink } from "@/components/Markdown/Link";
import { CodeSnippet } from "@/components/Markdown/CodeSnippet";

export default function SofiaBot() {
  // States
  const [chatIsOpen, setChatIsOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [chatMessages, setChatMessages] = React.useState([]);
  const [deletingMessages, setDeletingMessages] = React.useState(false);
  const [isMaximized, setIsMaximized] = React.useState(false); // Chatbot maximized

  // Refs
  const chatContentRef = React.useRef(null);
  // Custom hooks
  const { botIsWriting, getBotResponse } = useSofia();

  // * Open or close the chat
  const toggleChat = () => {
    setChatIsOpen(!chatIsOpen);
  };

  // * Generate an Id that will match a pair of user and bot messages (userN, botN)
  const generateId = (author) => {
    // Early return if empty array
    if (chatMessages.length === 0) return author.concat(1);
    // Array of messages IDs
    const idArray = chatMessages.map((m) => Number(m.id.replace(m.author, "")));
    // Next ID is current max + 1
    return author.concat(Math.max(...idArray) + 1);
  };

  // * Add message to chatMessages state
  const appendMessage = (message, author, error) => {
    setChatMessages((currentMessages) => [
      ...currentMessages,
      { content: message, author, error, id: generateId(author) },
    ]);
  };

  // * Expand chat
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  // Generate a random ID
  const generateRandomId = () => {
    return "maps-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  // Check and set user ID
  const setUserId = React.useCallback(() => {
    let userId = localStorage.getItem("userId");
    //console.log(`Retrieved userId from localStorage: ${userId}`);

    if (!userId) {
      userId = generateRandomId();
      localStorage.setItem("userId", userId);
      //console.log(`Generated and set new userId: ${userId}`);
    }

    return userId;
  }, []);

  // * Send and append the userMessage, then receive and append the botResponse
  const createUserInteraction = async (userMessage) => {
    // 1. Reset message state
    setMessage("");
    // 2. Append user message to message list
    appendMessage(userMessage, "user", false);

    const userId = setUserId();

    // 3. Get bot response
    const [botResponse, error] = await getBotResponse(userMessage, userId);
    // 4.a Append error message if error exists
    if (error || botResponse === null) {
      return appendMessage(
        "Parece que hay un error, vuelve a intentarlo más tarde",
        "bot",
        true,
      );
    }
    // 4. Success: append bot response to message list
    appendMessage(botResponse, "bot", false);
  };

  // * Receive userMessage and create an interaction with bot if message is not empty
  const handleMessage = async (e) => {
    e.preventDefault();
    // Early return if empty message is sent
    if (message === "") return;
    // User - Bot interaction
    await createUserInteraction(message);
  };

  // * Scroll to bottom depending on chatContent height
  const scrollToBottom = () => {
    // Scroll to the bottom
    chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
  };

  // * Check if there are stored messages in the localStorage to init state
  // and sert the localStorage at the first render
  React.useEffect(() => {
    // Get storedChatMessages from localStorage
    const storedMessages = localStorage.getItem("storedChatMessages");
    // Initialize chatMessages Stat if there are stored messages in localStorage
    if (storedMessages) {
      const parsedMessages = JSON.parse(storedMessages);
      setChatMessages(parsedMessages);
    }
    setUserId();
  }, [setUserId]);

  // * useEffect hook to scroll to the bottom after messages are updated
  React.useEffect(() => {
    if (chatMessages.length === 0) return;
    // Scroll whenever a message is appended and chatIsOpen is true
    if (chatIsOpen) {
      scrollToBottom();
    }
    // Store message in localStorage
    localStorage.setItem("storedChatMessages", JSON.stringify(chatMessages));
  }, [chatMessages]); // Run whenever chatMessages change

  // * useEffect hook to scroll to the bottom whenever chat is opened
  React.useEffect(() => {
    if (chatIsOpen) {
      scrollToBottom();
    }
  }, [chatIsOpen]);

  // * Delete error messages (bot and user) on demand
  const deleteErrorMessages = async (e, errorMessageId) => {
    e.preventDefault();
    // Disable chatbox input and send button
    setDeletingMessages(true);
    // Get userMessageId
    const userMessageId = errorMessageId.replace("bot", "user");
    const chatWithoutErrors = chatMessages
      .filter((message) => message.id !== errorMessageId)
      .filter((message) => message.id !== userMessageId);
    setDeletingMessages(false);
    // Update state
    setChatMessages(chatWithoutErrors);
  };

  // * Retry sending a message with error
  const retrySendMessage = async (e, errorMessageId) => {
    e.preventDefault();
    // Get userMessageId
    const userMessageId = errorMessageId.replace("bot", "user");
    // Get userMessage corresponding with userMessageId
    const foundUserMessage = chatMessages.find(
      (userMessage) => userMessage.id === userMessageId,
    );
    // Resend foundUsermessage content
    if (foundUserMessage !== null) {
      await createUserInteraction(foundUserMessage.content);
    }
  };

  const containsCode = (input) => {
    //console.log("Contains code input: ", input);
    return input?.includes("```");
  };

  function containsMarkdownLink(input) {
    const regex = /\[(.*?)\]\((.*?)\)/;
    return regex.test(input);
  }

  return (
    <>
      {/* ToggleBtn */}
      <button
        onClick={toggleChat}
        className={`${css.sofiaButton}
        right-[20px] bottom-[20px]
        sm:right-[50px] sm:bottom-[50px]`}
      >
        <img src="/img/Boton-ARIA.png" alt="sofia-profile" />
      </button>
      {/* <div className={css.chatContainer}> */}
      {/* Chatbot */}
      {chatIsOpen && (
        <div
          className={`fixed right-[20px] bottom-[calc(20px+50px+1rem)] font-sans rounded-lg z-[100001]
          transition-all duration-500 shadow-md animate-grow-bottom
          sm:right-[50px] sm:bottom-[calc(50px+50px+1rem)]
          ${
            isMaximized
              ? "w-chatbot-maximized h-[calc(100%-50px-50px-50px-1rem)]"
              : "w-chatbot-standard-mobile sm:w-chatbot-standard"
          }`}
        >
          <div className={css.chatHeader}>
            <h6>ARIA</h6>
            <div className=" flex items-center gap-4">
              <button className="hidden sm:inline" onClick={toggleMaximize}>
                {isMaximized ? (
                  <CloseFullscreenIcon className="!w-5 hover:text-gray-4 transition-colors duration-500 ease-in-out text-gray-5" />
                ) : (
                  <OpenInFullIcon className="!w-5 hover:text-gray-4 transition-colors duration-500 ease-in-out text-gray-5" />
                )}
              </button>
              <button onClick={toggleChat}>
                <CloseIcon className="!w-6 hover:text-accent-light transition-colors duration-500 ease-in-out text-accent-dark" />
              </button>
            </div>
          </div>
          <div
            ref={chatContentRef}
            className={`${css.chatContent} ${isMaximized ? " max-h-[-webkit-fill-available] h-[calc(100%-14px-1.5rem-14px-2rem-35px);]" : "max-h-[400px]"} `}
          >
            {/* ARIA Intro Message */}
            <div className={css.botReply}>
              <img src="/img/Boton-ARIA.png" alt="bot-profile" />
              <div>
                ¡Hola! Soy <strong>ARIA</strong>, tu asistente de inteligencia
                artificial en Universidad Tecmilenio. Estoy aquí para responder
                todas tus dudas sobre el Modelo Educativo <strong>MAPS</strong>{" "}
                y ayudarte a conocer todo lo que necesitas saber. ¿En qué puedo
                apoyarte hoy? 😊
              </div>
            </div>
            {/* Conditional lodaing */}
            {botIsWriting && (
              <div className={css.botLoading}>
                <img src="/img/Boton-ARIA.png" alt="bot-profile" />
                {/* Loading Bubble */}
                <div className={css.loadingBubble}>
                  <div className={css.firstDot}></div>
                  <div className={css.middleDot}></div>
                  <div className={css.lastDot}></div>
                </div>
              </div>
            )}
            {/* Dynamic messages: User and Bot */}
            {chatMessages.map((chatMessage) => {
              {
                /* User Message */
              }
              if (chatMessage.author === "user") {
                return (
                  <div key={chatMessage.id} className={css.userMessage}>
                    {chatMessage.content}
                  </div>
                );
                {
                  /* Bot response */
                }
              } else if (!chatMessage.error) {
                return (
                  <div key={chatMessage.id} className={css.botReply}>
                    <img src="/img/Boton-ARIA.png" alt="bot-profile" />
                    {containsCode(chatMessage?.content) ? (
                      <CodeSnippet
                        code={chatMessage.content}
                        customStyles={{
                          width: "fit-content",
                          maxWidth: "70%",
                          borderRadius: "0.5rem",
                          fontSize: "12px",
                          margin: 0,
                        }}
                      />
                    ) : containsMarkdownLink(chatMessage?.content) ? (
                      <MarkdownLink content={chatMessage.content} />
                    ) : (
                      <MarkdownLink content={chatMessage.content} />
                    )}
                  </div>
                );
                {
                  /* Bot Error */
                }
              } else {
                return (
                  <div key={chatMessage.id} className={css.botError}>
                    <img src="/img/Boton-ARIA.png" alt="bot-profile" />
                    <div className={css.errorBubble}>
                      <p>{chatMessage.content}</p>
                      <div className={css.errorActions}>
                        <button
                          className={css.retryButton}
                          onClick={(e) => retrySendMessage(e, chatMessage.id)}
                        >
                          Reintentar
                        </button>
                        <button
                          className={css.deleteButton}
                          onClick={(e) =>
                            deleteErrorMessages(e, chatMessage.id)
                          }
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
          {/* Chatbox */}
          <form onSubmit={handleMessage} className={css.chatBox}>
            <input
              disabled={botIsWriting || deletingMessages}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              type="text"
              name="message"
              className="text-dark-og-1"
              placeholder="Escribe..."
            />
            <button
              disabled={message === "" || deletingMessages}
              className={css.sendButton}
              type="submit"
            >
              <img src="/img/Boton-ARIA.png" alt="chevron" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
